<script setup lang="ts">
import { RouterView, RouterLink, useRoute } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useBuildsStore } from './stores/builds';
import { useBuildsEvents } from './composables/useBuildsEvents';
import { computed, ref, watch } from 'vue';
import ThemeToggle from './components/ThemeToggle.vue';

const authStore = useAuthStore();
const buildsStore = useBuildsStore();
const route = useRoute();

const isAuthenticated = computed(() => authStore.isAuthenticated);
const isAdmin = computed(() => authStore.isAdmin);
const userName = computed(() => authStore.user?.name || authStore.user?.preferredUsername || 'User');
const isLoginPage = computed(() => route.name === 'login');
const showHeader = computed(() => !isLoginPage.value && authStore.initialized);

// App-level SSE connection for real-time build updates
const { connect: connectSSE, disconnect: disconnectSSE } = useBuildsEvents({
  onBuildCreated: (build) => {
    buildsStore.addBuild(build);
  },
  onBuildUpdated: (build) => {
    buildsStore.applyBuildUpdate(build);
  },
  onBuildStagesUpdated: (id, stages) => {
    buildsStore.updateBuildStages(id, stages);
  },
});

watch(isAuthenticated, (authenticated) => {
  if (authenticated) {
    connectSSE();
  } else {
    disconnectSSE();
  }
}, { immediate: true });

// Mobile menu state
const mobileMenuOpen = ref(false);

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

// Admin dropdown state (desktop). On mobile the submenu is always
// inlined — see the .admin-menu rules in the <style> block — so the
// flag is effectively desktop-only.
const adminMenuOpen = ref(false);
const adminRouteNames = ['workers', 'pod-templates', 'status'];
const isAdminRouteActive = computed(() =>
  typeof route.name === 'string' && adminRouteNames.includes(route.name),
);

function toggleAdminMenu() {
  adminMenuOpen.value = !adminMenuOpen.value;
}

function closeAdminMenu() {
  adminMenuOpen.value = false;
}

function onAdminMenuKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') closeAdminMenu();
}

function onDocumentClick(e: MouseEvent) {
  // Any click outside the dropdown dismisses it. Clicks on the trigger
  // and on items inside carry `.stop` / their own close handlers so
  // they don't bubble here.
  const target = e.target as HTMLElement | null;
  if (!target?.closest('.admin-dropdown')) closeAdminMenu();
}

watch(adminMenuOpen, (open) => {
  if (open) {
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onAdminMenuKeydown);
  } else {
    document.removeEventListener('mousedown', onDocumentClick);
    document.removeEventListener('keydown', onAdminMenuKeydown);
  }
});

// Close menus on route change
watch(route, () => {
  closeMobileMenu();
  closeAdminMenu();
});
</script>

<template>
  <div class="app">
    <div v-if="!authStore.initialized" class="loading-screen">
      <div class="loading-content">
        <h1>Kiln</h1>
        <p>Loading...</p>
      </div>
    </div>
    <template v-else>
      <header class="header" v-if="showHeader">
        <div class="header-content">
          <RouterLink to="/" class="logo" @click="closeMobileMenu">
            <img src="./assets/logo.png" alt="Kiln" class="logo-icon" />
            Kiln
          </RouterLink>
          <button
            v-if="isAuthenticated"
            class="hamburger"
            :class="{ active: mobileMenuOpen }"
            @click="toggleMobileMenu"
            aria-label="Toggle menu"
          >
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </button>
          <nav class="nav" :class="{ open: mobileMenuOpen }" v-if="isAuthenticated">
            <RouterLink to="/" class="nav-link" @click="closeMobileMenu">Builds</RouterLink>
            <RouterLink to="/repositories" class="nav-link" @click="closeMobileMenu">Repositories</RouterLink>
            <RouterLink to="/schedules" class="nav-link" @click="closeMobileMenu">Schedules</RouterLink>
            <RouterLink to="/insights" class="nav-link" @click="closeMobileMenu">Insights</RouterLink>
          </nav>
          <div class="user-info" :class="{ open: mobileMenuOpen }" v-if="isAuthenticated">
            <a href="/docs/" class="docs-link" target="_blank" rel="noopener">Docs</a>
            <RouterLink to="/profile" class="user-link" @click="closeMobileMenu">{{ userName }}</RouterLink>
            <ThemeToggle />
            <div class="admin-dropdown" :class="{ open: adminMenuOpen }">
              <button
                type="button"
                class="admin-trigger"
                :class="{ 'router-link-active': isAdminRouteActive }"
                :aria-expanded="adminMenuOpen"
                aria-haspopup="true"
                @click.stop="toggleAdminMenu"
              >
                Admin
                <span class="admin-caret" aria-hidden="true">▾</span>
              </button>
              <div class="admin-menu" role="menu">
                <span class="admin-menu-label">Operations</span>
                <RouterLink to="/workers" class="admin-menu-item" role="menuitem" @click="closeMobileMenu(); closeAdminMenu();">Workers</RouterLink>
                <template v-if="isAdmin">
                  <span class="admin-menu-label">Configuration</span>
                  <RouterLink to="/pod-templates" class="admin-menu-item" role="menuitem" @click="closeMobileMenu(); closeAdminMenu();">Pod Templates</RouterLink>
                  <RouterLink to="/status" class="admin-menu-item" role="menuitem" @click="closeMobileMenu(); closeAdminMenu();">Status</RouterLink>
                </template>
              </div>
            </div>
            <button @click="authStore.logout" class="logout-btn">Logout</button>
          </div>
        </div>
      </header>
      <main class="main" :class="{ 'main-full': isLoginPage }">
        <RouterView :key="route.fullPath" />
      </main>
    </template>
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  transition: var(--theme-transition);
}

.loading-screen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-loading-gradient-start) 0%, var(--color-loading-gradient-end) 100%);
  color: var(--color-header-text);
}

.loading-content {
  text-align: center;
}

.loading-content h1 {
  margin: 0 0 16px 0;
  font-size: 2rem;
}

.loading-content p {
  margin: 0;
  color: var(--color-header-text-muted);
}

.header {
  background-color: var(--color-header-bg);
  color: var(--color-header-text);
  padding: 0 20px;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  height: 60px;
  gap: 40px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-header-text);
  text-decoration: none;
}

.logo-icon {
  height: 28px;
  width: auto;
}

.nav {
  display: flex;
  gap: 20px;
}

.nav-link {
  color: var(--color-header-text-muted);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 4px;
  transition: all 0.2s;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: var(--color-header-text);
  background-color: var(--color-header-hover);
}

/* ---- Admin dropdown (desktop, anchored right) ---- */

.admin-dropdown {
  position: relative;
  display: flex;
  align-items: center;
}

.admin-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--color-header-text-muted);
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  transition: color 0.2s;
}

.admin-trigger:hover,
.admin-trigger.router-link-active {
  color: var(--color-header-text);
}

.admin-caret {
  font-size: 0.7em;
  transition: transform 0.15s ease;
}

.admin-dropdown.open .admin-caret {
  transform: rotate(180deg);
}

.admin-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 200px;
  background: var(--color-card-bg);
  border: 1px solid var(--color-card-border);
  border-radius: 6px;
  box-shadow: 0 6px 20px var(--color-card-shadow);
  padding: 6px;
  display: none;
  flex-direction: column;
  gap: 1px;
  z-index: 100;
}

.admin-dropdown.open .admin-menu {
  display: flex;
}

.admin-menu-label {
  padding: 6px 12px 2px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-secondary);
}

.admin-menu-item {
  display: block;
  padding: 8px 12px;
  border-radius: 4px;
  color: var(--color-text-primary);
  text-decoration: none;
  font-size: 0.95rem;
  transition: background-color 0.15s;
}

.admin-menu-item:hover {
  background-color: var(--color-header-hover);
}

.admin-menu-item.router-link-active {
  background-color: var(--color-header-hover);
  color: var(--color-text-primary);
  font-weight: 500;
}

.user-info {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.docs-link,
.user-link {
  color: var(--color-header-text-muted);
  text-decoration: none;
  transition: color 0.2s;
}

.docs-link:hover,
.user-link:hover {
  color: var(--color-header-text);
}

.logout-btn {
  background: transparent;
  border: 1px solid var(--color-header-border);
  color: var(--color-header-text-muted);
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: var(--color-header-hover);
  color: var(--color-header-text);
  border-color: var(--color-header-text);
}

.main {
  flex: 1;
  padding: 20px;
  background-color: var(--color-bg-primary);
}

.main-full {
  padding: 0;
}

/* Hamburger menu button */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  margin-left: auto;
}

.hamburger-line {
  display: block;
  width: 24px;
  height: 2px;
  background-color: var(--color-header-text);
  transition: transform 0.3s, opacity 0.3s;
  margin: 3px 0;
}

.hamburger.active .hamburger-line:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.hamburger.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.hamburger.active .hamburger-line:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

/* Tablet breakpoint (768px) */
@media (max-width: 768px) {
  .hamburger {
    display: flex;
  }

  .header-content {
    flex-wrap: wrap;
    height: auto;
    min-height: 60px;
    padding: 10px 0;
    gap: 0;
  }

  .logo {
    order: 1;
  }

  .hamburger {
    order: 2;
  }

  .nav {
    order: 3;
    display: none;
    flex-direction: column;
    width: 100%;
    gap: 0;
    padding-top: 10px;
  }

  .nav.open {
    display: flex;
  }

  .nav-link {
    padding: 12px 16px;
    border-radius: 0;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  /* Inline the admin group instead of using a dropdown on mobile */
  .admin-dropdown {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .admin-trigger {
    display: none;
  }

  .admin-menu {
    position: static;
    display: flex;
    flex-direction: column;
    background: transparent;
    border: none;
    box-shadow: none;
    border-radius: 0;
    padding: 0;
    min-width: 0;
  }

  .admin-menu-label {
    padding: 10px 16px 4px;
  }

  .admin-menu-item {
    padding: 12px 24px;
    border-radius: 0;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  .user-info {
    order: 4;
    display: none;
    flex-direction: column;
    width: 100%;
    gap: 8px;
    padding: 12px 0;
    border-top: 1px solid var(--color-header-border);
    margin-left: 0;
  }

  .user-info.open {
    display: flex;
  }

  .docs-link,
  .user-link {
    padding: 8px 16px;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  .logout-btn {
    width: 100%;
    padding: 12px 16px;
    min-height: 44px;
  }

  .main {
    padding: var(--mobile-padding, 12px);
  }
}

/* Phone breakpoint (480px) */
@media (max-width: 480px) {
  .header {
    padding: 0 var(--mobile-padding, 12px);
  }

  .main {
    padding: var(--mobile-padding, 12px);
  }
}
</style>
